import { getAccessToken, logout } from './auth';

export interface StockDeviceRef {
  id:          number;
  description: string;
}

export interface StockDeviceTicketRef {
  id:           number;
  order_number: string;
}

/** Zustand mit Sortierschlüssel aus den Stammdaten (display_order). */
export interface StockDeviceCondition extends StockDeviceRef {
  sort_order: number;
}

/** Jüngster Versand an einen Servicepartner — das nächste Ziel des Geräts. */
/**
 * Was der Servicepartner im Partnerportal eingetragen hat. `null`, solange er das
 * Gerät nicht übernommen hat.
 */
/**
 * Der Preisstand eines Falls. `reference_price` ist der freigegebene Preis aus
 * der Rezeptur, `amount` der Betrag, den der Partner bestätigt hat — die beiden
 * unterscheiden sich, wenn die Rezeptur nach der Bestätigung geändert wurde.
 * Einen eigenen Betrag kann der Partner nicht nennen.
 */
export interface StockDevicePartnerCost {
  /** `requested` = eine Preisanfrage liegt bei uns (Gerät auf „Warte auf Preisfreigabe"). */
  state:                 'open' | 'requested' | 'confirmed';
  /** Bezugspreis: zugesagter Betrag, sonst Rezeptur; Garantie 0,00; null = nichts hinterlegt. */
  reference_price:       number | null;
  reference_price_label: string;
  amount:                number | null;
  amount_label:          string;
  confirmed_timestamp:   string | null;
  /** Zusage aus einer Preisanfrage — ersetzt die Rezeptur als Bezugspreis dieses Falls. */
  approved_amount:       number | null;
  approved_amount_label: string;
  /** Die aktuelle Anfrage samt Entscheidung; null, wenn nie angefragt wurde. */
  price_request:         StockDevicePriceRequest | null;
}

export interface StockDevicePriceRequest {
  amount:             number;
  amount_label:       string;
  note:               string;
  timestamp:          string | null;
  decision:           'accepted' | 'rejected' | null;
  decision_note:      string | null;
  decision_timestamp: string | null;
}

/** Status samt Badge-Farben (aus den Stammdaten) und Zeitpunkt des letzten Wechsels. */
export interface StockDeviceStatus extends StockDeviceRef {
  /** Leer = Akzentfarbe der Oberfläche. */
  background_color:  string;
  foreground_color:  string;
  changed_timestamp: string | null;
}

/** FMIP-Stand zur aktuellen IMEI — wie im Ticket. */
export interface StockDeviceFmip {
  status:             'active' | 'inactive' | 'unknown';
  checked_at:         string | null;
  device_description: string | null;
}

/** Ein IMEI-Wechsel des Lagergeräts (heute nur SWAP im Partnerportal). */
export interface StockDeviceImeiChange {
  previous_imei_sn: string | null;
  new_imei_sn:      string | null;
  reason:           string;
  change_timestamp: string;
}

export interface StockDevicePartnerFile {
  id:                 number;
  category:           string;
  category_label:     string;
  description:        string;
  extension:          string;
  filesize:           number;
  creation_timestamp: string;
}

/**
 * Was der Partner im Portal eingetragen hat. Bewusst schmal — wer wann was getan
 * hat, steht im Verlauf des Geräts.
 */
export interface StockDevicePartnerCase {
  id:                       number;
  partner_reference:        string | null;
  expected_completion_date: string | null;
  cost:                     StockDevicePartnerCost;
  /** Was der Partner im Portal hochgeladen hat — Testbericht, Schein, Rechnung. */
  files:                    StockDevicePartnerFile[];
  /** Ergebnis des Abschlusses samt Text; null solange offen. */
  outcome:                  'repaired' | 'not_repaired' | null;
  outcome_note:             string | null;
  /** Grading des Partners nach der Reparatur (A+–D), nur bei „repariert". */
  grading:                  string | null;
  completed_timestamp:      string | null;
}

/**
 * Status, in denen das Ergebnis des Partners neben dem Status steht: beim Partner
 * fertig (4/8), auf dem Rückweg (5), zurück (6), abgeschlossen (9). Bei einem
 * erneuten Versand (1–3, 7) gehört das alte Ergebnis nicht mehr dazu.
 */
export const PARTNER_OUTCOME_STATUS_IDS = [4, 5, 6, 8, 9];

export interface StockDeviceShipment {
  id:        number;
  /** true = vorbereitet und noch stornierbar, false = steht auf einem Lieferschein. */
  is_active: boolean;
  servicepartner:     StockDeviceRef;
  creation_timestamp: string;
}

/** Ein Lieferschein, auf dem das Gerät steht. */
export interface StockDeviceDeliverySlip {
  id:                 number;
  /** Anzeigenummer aus dem Kreis RL-8000. */
  slip_number:        string;
  creation_timestamp: string;
}

export interface StockDevice {
  id:                 number;
  /** Öffentliche Kennung, z.B. „R2-5481". */
  stock_number:       string;
  imei_sn:            string | null;
  /** Altgerät ohne Master: `master.description` ist die händische Bezeichnung, `master.id` null. */
  is_legacy_device:   boolean;
  alternative_device_description: string | null;
  vendor:             StockDeviceRef;
  master:             { id: number | null; description: string };
  /** null bei Altgeräten. */
  color:              StockDeviceRef | null;
  /** null bei Altgeräten. */
  memory_size:        number | null;
  memory_size_label:  string;
  condition:          StockDeviceCondition | null;
  status:             StockDeviceStatus;
  creation_timestamp: string;
  ticket:             StockDeviceTicketRef | null;
  /** Jüngster Versand. Bleibt stehen, wenn das Gerät schon dort ist — der Etikett-Nachdruck braucht ihn. */
  shipment:           StockDeviceShipment | null;
  /**
   * Wohin das Gerät als NÄCHSTES geht. Sobald der Partner es übernommen hat, ist
   * es angekommen und hat kein nächstes Ziel mehr — dann null.
   */
  next_destination:   StockDeviceShipment | null;
  /** Lieferscheine, auf denen das Gerät steht — neueste zuerst. */
  delivery_slips:     StockDeviceDeliverySlip[];
  partner_case:       StockDevicePartnerCase | null;
  /** Frühere IMEIs, neueste zuerst. Leer = die IMEI wurde nie gewechselt. */
  previous_imeis:     StockDeviceImeiChange[];
  /** null, wenn das Gerät keine IMEI/SN trägt. */
  fmip:                 StockDeviceFmip | null;
  /** Liegt zur aktuellen IMEI eine gespeicherte Detailauskunft vor? („Anzeigen" statt „Abfragen") */
  has_imei_information: boolean;
  /** Manueller Abschluss — nur bei Status „Abgeschlossen", sonst null. */
  closing:              StockDeviceClosing | null;
}

/** Manueller Abschluss eines Lagergeräts — endgültig. „Lagereingang HS" und „Ankauf HS.cc" buchen zusätzlich. */
export interface StockDeviceClosing {
  type:               StockDeviceRef;
  /** Wert netto; null = keiner angegeben (nicht 0). Bei „Lagereingang HS" der Handyshop-Einkaufspreis. */
  value_net_eur:      number | null;
  note:               string | null;
  user_name:          string;
  creation_timestamp: string;
  /** Nur bei „Lagereingang HS", sonst null. */
  hs_receipt:         StockDeviceHsReceipt | null;
  /** Nur bei „Ankauf HS.cc" mit Buchung, sonst null (auch bei älteren, rein dokumentierten Abschlüssen). */
  ankauf:             StockDeviceAnkauf | null;
}

/** Stand der Buchung „Ankauf HS.cc": Ankaufsrechnung an Assona, E-Artikel, Lieferscheine an den Ankauf. */
export interface StockDeviceAnkauf {
  /** false = auf halber Strecke abgebrochen, „Ankauf fortsetzen" möglich. */
  completed:                boolean;
  error:                    string | null;
  grading:                  string | null;
  invoice_number:           string | null;
  e_number:                 string | null;
  hs_delivery_slip_numbers: string[];
}

/** Stand der Buchung „Lagereingang HS": Einbuchung bei Repaircenter + Lieferschein an die HS Zentrale. */
export interface StockDeviceHsReceipt {
  /** false = auf halber Strecke abgebrochen, „Einbuchung fortsetzen" möglich. */
  completed:               boolean;
  error:                   string | null;
  shop_device_id:          number;
  shop_device_label:       string;
  shop_device_condition:   string | null;
  supplier_order_id:       number | null;
  hs_delivery_slip_number: string | null;
}

interface ApiEnvelope<T> {
  result: 'SUCCESS' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'NOT_FOUND' | 'BAD_REQUEST' | 'INTERNAL_ERROR' | 'METHOD_NOT_ALLOWED' | 'CONFLICT';
  reason?: string;
  fields?: string[];
  items?: T[];
  item?:  T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: ApiEnvelope<unknown>['result'] | 'network' | 'parse',
    readonly fields?: string[],
  ) {
    super(message);
  }
}

const BASE = `${window.location.origin}/api/stock-devices`;

async function request<T>(path: string, init: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }

  let resp: Response;
  try {
    resp = await fetch(path, {
      ...init,
      headers: {
        'Accept':        'application/json',
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${token}`,
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  if (resp.status === 401) {
    logout('none');
  }

  let body: ApiEnvelope<T>;
  try {
    body = (await resp.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(`Unlesbare Server-Antwort (${resp.status})`, resp.status, 'parse');
  }

  if (!resp.ok || body.result !== 'SUCCESS') {
    throw new ApiError(
      body.reason ?? `Server-Fehler ${resp.status}`,
      resp.status,
      body.result,
      body.fields,
    );
  }

  return body;
}

/**
 * Druckt die Etiketten der übergebenen Lagergeräte als einen Auftrag auf der
 * Peppi-Druckstation des Benutzers. Wirft mit der Klartextmeldung des Servers,
 * wenn kein Drucker hinterlegt ist oder der Auftrag nicht zugestellt wurde.
 */
export async function printLabels(ids: number[]): Promise<void> {
  await request<unknown>(`${BASE}/print-labels`, {
    method: 'POST',
    body:   JSON.stringify({ ids }),
  });
}

/**
 * Erlaubte Kombination für den Versand: ein Servicepartner mit den Fehlerbildern,
 * für die es zum Master des Geräts eine Rezeptur gibt.
 */
/**
 * Ein Fehlerbild, fuer das es zum Master eine Rezeptur gibt — samt Preis
 * (Aufschlag der Rezeptur, netto; `price_label` schon formatiert, z.B. „50,00 €").
 */
export interface ErrorPatternOption extends StockDeviceRef {
  /** null bei Altgeräten — dort gibt es keine Rezeptur, der Preis wird händisch eingegeben. */
  price_net_eur: number | null;
  price_label:   string;
}

export interface ServicepartnerOption {
  id:             number;
  description:    string;
  /** Interner Servicepartner: der Dialog bietet den Sofortigen LS-Abschluss an. */
  instant_slip_closing?: boolean;
  error_patterns: ErrorPatternOption[];
}

/** Partner der Garantie-Auswahl, mit demselben Kennzeichen wie bei den Rezepturen. */
export interface WarrantyServicepartnerOption extends StockDeviceRef {
  instant_slip_closing?: boolean;
}

/**
 * Ergebnis des Sofortigen LS-Abschlusses. `attempted` false: Partner ohne Flag
 * oder Häkchen abgewählt. `completed` false mit `message`: ein Folgeschritt ist
 * gescheitert, das Gerät steht im erreichten Zwischenstand.
 */
export interface InstantClosingResult {
  attempted:   boolean;
  completed:   boolean;
  slip_number: string | null;
  message:     string | null;
}

/**
 * Was der Garantieversand braucht: alle aktiven Servicepartner (nicht nur die mit
 * Rezeptur), der am Master hinterlegte Garantiepartner als Vorauswahl und die aus
 * dem Ticket vorbefüllte Fehlerbeschreibung.
 */
export interface WarrantyShipmentOptions {
  servicepartners:                    WarrantyServicepartnerOption[];
  default_servicepartner_location_id: number | null;
  damage_description:                 string;
  /** false bei Altgeräten — es gibt keinen Master, an den „als Standard" schreiben könnte. */
  can_save_as_default:                boolean;
}

export interface ShipmentOptions {
  recipes:  ServicepartnerOption[];
  warranty: WarrantyShipmentOptions;
  /** Altgerät: `recipes` ist die freie Stammdatenauswahl, der Netto-Preis ist Pflicht. */
  manual_price_required: boolean;
  manual_price_max:      number;
}

/** Obergrenze des händischen Preises (Altgeräte), wie R2RecipePrices::MANUAL_PRICE_MAX. */
export const MANUAL_PRICE_MAX = 9999.99;

/**
 * Liest einen händisch eingegebenen Netto-Preis („45,50" oder „45.5"). null bei leerer
 * oder ungültiger Eingabe sowie außerhalb 0,01 … MANUAL_PRICE_MAX bzw. mit mehr als
 * zwei Nachkommastellen — dieselbe Regel, die der Server prüft.
 */
export function parseManualPrice(text: string): number | null {
  const normalized = text.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value) || value <= 0 || value > MANUAL_PRICE_MAX) return null;
  return value;
}

/** Höchstlänge der Fehlerbeschreibung — mehr passt nicht auf die zweite Etikettenseite. */
export const DAMAGE_DESCRIPTION_MAX_LENGTH = 500;

export async function listServicepartnerOptions(deviceId: number): Promise<ShipmentOptions> {
  const body = await request<ServicepartnerOption>(`${BASE}/${deviceId}/servicepartner-options`, { method: 'GET' });
  const envelope = body as ApiEnvelope<ServicepartnerOption> & { warranty?: WarrantyShipmentOptions };
  const extra    = body as { manual_price_required?: boolean; manual_price_max?: number };
  return {
    recipes:  envelope.items ?? [],
    warranty: envelope.warranty ?? {
      servicepartners:                    [],
      default_servicepartner_location_id: null,
      damage_description:                 '',
      can_save_as_default:                true,
    },
    manual_price_required: extra.manual_price_required === true,
    manual_price_max:      extra.manual_price_max ?? MANUAL_PRICE_MAX,
  };
}

/** Ergebnis eines Versands. `printed` false heißt: angelegt, aber nicht gedruckt. */
export interface ServicepartnerShipmentResult {
  id:            number;
  printed:       boolean;
  print_warning: string | null;
  instant_closing?: InstantClosingResult;
}

/**
 * Versand an einen Servicepartner: der Server druckt das Etikett und legt die
 * Versandzeile an. Druckt die Station nicht, ist der Versand trotzdem angelegt
 * (`printed` false, `print_warning` gesetzt) — der Druckauftrag liegt in der
 * Warteschlange.
 *
 * Wirft mit der Klartextmeldung des Servers, wenn kein Drucker hinterlegt ist
 * oder es zur Kombination keine Rezeptur gibt — dann ist nichts gespeichert.
 *
 * Auf Garantie (`is_warranty`) entfällt das Fehlerbild; dafür ist
 * `damage_description` Pflicht. `save_as_master_default` schreibt den gewählten
 * Partner als Garantiepartner an den Masterartikel.
 */
export async function createServicepartnerShipment(
  deviceId: number,
  payload: {
    servicepartner_location_id: number;
    error_pattern_id?:          number | null;
    is_warranty?:               boolean;
    damage_description?:        string;
    save_as_master_default?:    boolean;
    /** Nur bei Altgeräten (dort Pflicht); bei Geräten mit Master lehnt der Server ihn ab. */
    manual_price_net_eur?:      number | null;
    /** Nur bei internen Servicepartnern wirksam; fehlt es, gilt true. */
    instant_slip_closing?:      boolean;
  },
): Promise<ServicepartnerShipmentResult> {
  const body = await request<ServicepartnerShipmentResult>(`${BASE}/${deviceId}/servicepartner-shipment`, {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

/**
 * Nimmt ein vorbereitetes Gerät wieder aus dem Versand — die offenen Versandzeilen
 * werden gelöscht. Nur möglich, solange das Gerät auf keinem Lieferschein steht.
 * Derselbe Weg für „Entfernen" im Lieferschein-Modal und „Versand stornieren".
 */
export async function deleteServicepartnerShipment(deviceId: number): Promise<void> {
  await request<unknown>(`${BASE}/${deviceId}/servicepartner-shipment`, { method: 'DELETE' });
}

/** Ergebnis eines Etiketten-Nachdrucks. */
export interface ShipmentLabelReprintResult {
  shipment_id:   number;
  printed:       boolean;
  print_warning: string | null;
}

/**
 * Druckt das Etikett des jüngsten Versands erneut. Legt keinen neuen Versand an und
 * ändert den Gerätestatus nicht — es entsteht nur Papier.
 */
export async function reprintServicepartnerShipmentLabel(deviceId: number): Promise<ShipmentLabelReprintResult> {
  const body = await request<ShipmentLabelReprintResult>(
    `${BASE}/${deviceId}/servicepartner-shipment/reprint`,
    { method: 'POST' },
  );
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

/**
 * Lädt den Lieferschein als PDF. Kein Umweg über ein <a href>, weil der Endpunkt
 * ein Bearer-Token braucht — die Antwort kommt als Blob und wird vom Aufrufer
 * geöffnet oder gespeichert.
 */
export async function fetchDeliverySlipPdfBlob(slipId: number): Promise<Blob> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }

  let resp: Response;
  try {
    resp = await fetch(`${window.location.origin}/api/delivery-slips/${slipId}/pdf`, {
      method:  'GET',
      headers: { 'Authorization': `Bearer ${token}` },
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  if (resp.status === 401) {
    logout('none');
  }
  if (!resp.ok) {
    throw new ApiError(`PDF nicht abrufbar (${resp.status})`, resp.status, 'NOT_FOUND');
  }
  return await resp.blob();
}

/**
 * Ergebnis einer Korrektur. `changed` false heißt: die Eingabe entsprach dem, was
 * ohnehin dastand — dann ist auch kein Verlaufseintrag entstanden.
 */
export interface StockDeviceCorrectionResult {
  id:              number;
  changed:         boolean;
  old_description: string | null;
  new_description: string | null;
}

/**
 * Korrigiert die Gerätedaten eines Lagergeräts (Master, Farbe, Speichergröße,
 * Zustand). Wirkt NUR auf das Lagergerät — das Herkunftsticket bleibt, wie es ist.
 *
 * Wirft mit der Klartextmeldung des Servers, wenn der Master nicht freigegeben ist,
 * die Variante nicht existiert oder ein Versand vorbereitet ist und der Master
 * gewechselt werden soll — dann ist nichts gespeichert.
 */
export async function correctDevice(
  deviceId: number,
  payload: {
    device_id:    number | null;
    color_id:     number | null;
    memory_size:  number | null;
    condition_id: number | null;
    /** Altgerät: Bezeichnung statt Master — dann sind device_id/color_id/memory_size null. */
    alternative_device_description?: string | null;
  },
): Promise<StockDeviceCorrectionResult> {
  const body = await request<StockDeviceCorrectionResult>(`${BASE}/${deviceId}/device`, {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

/** Ein Verlaufseintrag des Lagergeräts. */
export interface StockDeviceHistoryEntry {
  id:                 number;
  note:               string;
  json_data:          Record<string, unknown> | null;
  user_id:            number;
  user_name:          string;
  creation_timestamp: string;
}

/** Verlauf eines Lagergeräts, neueste zuerst. */
export async function listHistory(deviceId: number): Promise<StockDeviceHistoryEntry[]> {
  const body = await request<StockDeviceHistoryEntry>(`${BASE}/${deviceId}/history`, { method: 'GET' });
  return body.items ?? [];
}

/**
 * Abschlussart für „Manueller Abschluss"; `requires_note` = Notiz Pflicht (Sonstiges),
 * `books_hs_receipt` = bucht bei Repaircenter ein und schickt an die HS Zentrale (Lagereingang HS).
 */
export interface StockDeviceClosingType extends StockDeviceRef {
  requires_note:    boolean;
  books_hs_receipt: boolean;
  /** Ankauf HS.cc: Ankaufsrechnung an Assona, E-Artikel, Lieferschein an den Ankauf. */
  books_ankauf_invoice: boolean;
}

export interface StockDeviceGrading {
  id:      number;
  grading: string;
}

/** Grading-Auswahl und Preisvorschlag für „Ankauf HS.cc". */
export interface StockDeviceAnkaufPrice {
  /** Gesetzt = Abschluss gesperrt (kein Master, keine IMEI). */
  blocked_reason:         string | null;
  /** Grading aus dem aktuellen Gerätezustand. */
  preselected_grading_id: number | null;
  grading_id:             number | null;
  /** null = keine Ankaufspreise am Master — der Preis wird von Hand eingegeben. */
  suggested_price:        number | null;
  gradings:               StockDeviceGrading[];
}

/** Preisvorschlag für ein Grading; ohne gradingId gilt das Grading aus dem Gerätezustand. */
export async function getAnkaufPrice(deviceId: number, gradingId: number | null): Promise<StockDeviceAnkaufPrice> {
  const query = gradingId !== null ? `?grading_id=${gradingId}` : '';
  const body = await request<StockDeviceAnkaufPrice>(`${BASE}/${deviceId}/ankauf-price${query}`, { method: 'GET' });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

/** Wählbarer Artikel für „Lagereingang HS". */
export interface StockDeviceHsReceiptArticle {
  id:            number;
  label:         string;
  condition_id:  number;
  condition:     string;
  is_eol:        boolean;
  /** Handyshop-Einkaufspreis; null = keiner hinterlegt (dann nicht buchbar). */
  hs_ek_price:   number | null;
  price_missing: boolean;
}

export interface StockDeviceHsReceiptCandidates {
  /** Gesetzt = Abschluss gesperrt (kein Master, keine IMEI, kein Refurbished-Artikel). */
  blocked_reason:        string | null;
  preselected_device_id: number | null;
  devices:               StockDeviceHsReceiptArticle[];
}

/** Artikelauswahl für „Lagereingang HS" — auch für „Aktualisieren" nach dem Anlegen eines Artikels. */
export async function getHsReceiptCandidates(deviceId: number): Promise<StockDeviceHsReceiptCandidates> {
  const body = await request<StockDeviceHsReceiptCandidates>(`${BASE}/${deviceId}/hs-receipt-candidates`, { method: 'GET' });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

/** Abschlussarten in Reihenfolge der Stammdaten. */
export async function listClosingTypes(): Promise<StockDeviceClosingType[]> {
  const body = await request<StockDeviceClosingType>(`${window.location.origin}/api/stock-device-closing-types`, { method: 'GET' });
  return body.items ?? [];
}

export interface StockDeviceClosingResult {
  id:                  number;
  closing_id:          number;
  /** Anzahl der mit stornierten vorbereiteten Versände. */
  cancelled_shipments: number;
  /** Buchende Abschlussart, sonst null. */
  booking_kind?:       'hs_receipt' | 'ankauf' | null;
  /** Nur „Lagereingang HS": */
  supplier_order_id?:       number | null;
  hs_delivery_slip_number?: string | null;
  /** Nur „Ankauf HS.cc": */
  invoice_number?:           string | null;
  e_number?:                 string | null;
  hs_delivery_slip_numbers?: string[];
  /** Druck fehlgeschlagen oder kein Drucker — der Abschluss gilt trotzdem. */
  print_warning?:           string | null;
  /** Buchung unvollständig — Gerät ist trotzdem abgeschlossen, „Einbuchung fortsetzen". */
  incomplete_error?:        string | null;
}

/**
 * Schließt ein Lagergerät manuell ab. Ein vorbereiteter Versand wird dabei
 * storniert. Wirft mit der Klartextmeldung des Servers (falscher Status, schon
 * abgeschlossen, Notiz fehlt, ungültiger Wert) — dann ist nichts gespeichert.
 */
export async function closeDevice(
  deviceId: number,
  payload: { closing_type_id: number; value_net_eur: string | null; note: string | null; shop_device_id?: number | null; grading_id?: number | null },
): Promise<StockDeviceClosingResult> {
  const body = await request<StockDeviceClosingResult>(`${BASE}/${deviceId}/closing`, {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

/**
 * Setzt eine abgebrochene Buchung „Lagereingang HS" bzw. „Ankauf HS.cc" fort. Erledigte Schritte erkennt der
 * Server selbst; bleibt sie unvollständig, steht der Grund in `incomplete_error`.
 */
export async function resumeHsReceipt(deviceId: number): Promise<StockDeviceClosingResult> {
  const body = await request<StockDeviceClosingResult>(`${BASE}/${deviceId}/closing/resume`, { method: 'POST' });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

export async function list(): Promise<StockDevice[]> {
  const body = await request<StockDevice>(BASE, { method: 'GET' });
  return body.items ?? [];
}

/** `device_status.id` des ersten Status („Neu"). */
export const STOCK_STATUS_NEU = 1;

/** `device_status.id` eines Geräts, das der Servicepartner zurückgeschickt hat. */
export const STOCK_STATUS_VOM_SERVICEPARTNER_ZURUECK = 6;

/**
 * Aus diesen beiden Status darf ein Gerät an einen Servicepartner gehen: frisch
 * eingebucht oder vom Partner zurück — ein Rückläufer, der noch nicht in Ordnung
 * ist, geht ein zweites Mal hinaus. Dazwischen ist es unterwegs oder beim Partner.
 * Dieselbe Regel prüft der Server (`StockDeviceStatusModel::isShippable`).
 */
export function isShippableStatus(statusId: number | undefined): boolean {
  return statusId === STOCK_STATUS_NEU || statusId === STOCK_STATUS_VOM_SERVICEPARTNER_ZURUECK;
}

export const STOCK_STATUS_VERSAND_ZU_SERVICEPARTNER = 2;

/** `device_status.id` eines manuell abgeschlossenen Geräts — Endstatus, eingefroren. */
export const STOCK_STATUS_ABGESCHLOSSEN = 9;

/**
 * Abschließen kann man nur, was bei uns liegt: frisch eingebucht oder vom Partner
 * zurück. Dieselbe Regel prüft der Server (`StockDeviceStatusModel::isClosable`).
 */
export function isClosableStatus(statusId: number | undefined): boolean {
  return statusId === STOCK_STATUS_NEU || statusId === STOCK_STATUS_VOM_SERVICEPARTNER_ZURUECK;
}

/**
 * Nur bis hierher hat ein Etikett-Nachdruck einen Zweck: vorbereitet oder
 * unterwegs (Etikett verloren). Sobald der Partner das Gerät hat — und zurück im
 * Lager erst recht — ist das Papier wertlos. Dieselbe Regel prüft der Server
 * (`StockDeviceStatusModel::isReprintable`).
 */
export function isReprintableStatus(statusId: number | undefined): boolean {
  return statusId === STOCK_STATUS_NEU || statusId === STOCK_STATUS_VERSAND_ZU_SERVICEPARTNER;
}

export interface StockDeviceImeiInformation {
  html:       string;
  service_id: number;
  checked_at: string;
}

/** Gespeicherte IMEI-Detailauskunft zur aktuellen IMEI des Lagergeräts; 404 = nichts gespeichert. */
export async function getImeiInformation(deviceId: number): Promise<StockDeviceImeiInformation> {
  const body = await request<StockDeviceImeiInformation>(`${BASE}/${deviceId}/imei-information`, { method: 'GET' });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

/** Kostenpflichtige Abfrage — nie automatisch, nur auf Klick. */
export async function fetchImeiInformation(deviceId: number): Promise<StockDeviceImeiInformation> {
  const body = await request<StockDeviceImeiInformation>(`${BASE}/${deviceId}/imei-information/fetch`, { method: 'POST' });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

/**
 * Liest aus einem Scan die Lagernummer heraus und gibt sie normalisiert zurück
 * („R2-5481"), sonst null. Der Handscanner liefert je nach Etikett „R2-5481",
 * „r2 5481" oder nur „5481"; „RD-3000" bleibt gültig, weil die vor der
 * Umstellung gedruckten Etiketten noch an den Geräten hängen.
 */
export function parseStockNumber(raw: string): string | null {
  const match = raw.trim().toUpperCase().match(/^(R2|RD)?[\s-]*(\d{1,9})$/);
  if (!match) return null;
  const id = Number.parseInt(match[2], 10);
  if (!Number.isFinite(id) || id <= 0) return null;
  return `${match[1] ?? 'R2'}-${id}`;
}

/** Binaerinhalt eines Partner-Dokuments MIT Bearer-Token (fuer Blob-Open im neuen Tab). */
export async function fetchPartnerCaseFileBlob(deviceId: number, fileId: number): Promise<Blob> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }

  let resp: Response;
  try {
    resp = await fetch(`${BASE}/${deviceId}/partner-case/files/${fileId}`, {
      method:  'GET',
      headers: { 'Authorization': `Bearer ${token}` },
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  if (!resp.ok) {
    throw new ApiError(`Dokument nicht abrufbar (${resp.status})`, resp.status, 'NOT_FOUND');
  }
  return await resp.blob();
}
